import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { ListagemArquivosActions } from 'Store/ListagemArquivos/ListagemArquivos.actions';
import { queryClient } from 'Context/QueryClientContext';
import { useQueryVerifyAutodeskProfressFiles } from './useQueryVerifyAutodeskProfressFiles';
import { ApisEnum } from 'Data/enums/Apis';
import { FILES_QUERY_KEY, OBRA_QUERY_KEY } from 'Data/keys/QueryKeys';
import ObrasApi from 'Data/Obras.api';
import { IListFilesRequest } from 'Data/interfaces/ListagemArquivos/IListFilesRequest';
import {
  IUpdateStatus,
  IUpdateStatusStateRequest,
} from 'Data/interfaces/ListagemArquivos/IUpdateStatus';
import { BucketFileStatusEnum, IFileData } from 'Data/interfaces/ListagemArquivos/IListFilesResponse';
import { IObraResponse } from 'Data/interfaces/Obra/IObraListResponse';
import { IUseQueryFilesReturn } from 'Data/interfaces/ListagemArquivos/IListFiles';
import { ISignalRArchive } from 'Data/interfaces/Upload/IUploadFileData';
import { IAutodeskViewer } from 'Data/interfaces/Viewer/IAutodeskViewer';
import { ViewerEngineName, withConversionStatus } from 'Data/interfaces/Viewer/INativeViewerArtifacts';
import { Mixpanel } from 'Utils/MixPanel';
import AppStorage from 'Utils/AppStorage';
import toastHandler from 'Utils/toastHandler';

type LoadObsoleteFilesType = Record<string, boolean>;

export const useQueryFiles = (request: IListFilesRequest) => {
  const dispatch = useDispatch();
  const {
    verifyAutodeskProfressFiles,
  } = useQueryVerifyAutodeskProfressFiles();

  const [verifyFilesIds, setVerifyFilesIds] = useState<string[]>([]);
  const [loadObsoleteFiles, setLoadObsoleteFiles] = useState<LoadObsoleteFilesType>({});
  // Quando a pasta pede os arquivos obsoletos, é feita uma segunda chamada logo
  // depois da primeira. Ela roda em background: já existe lista na tela, então o
  // loading não deve reaparecer. A ref é lida na renderização (sem atraso de
  // estado) para não piscar o skeleton entre o refetch e a resposta.
  const isBackgroundRefetching = useRef(false);

  const { data, refetch, isLoading, isFetching, error } = useQuery({
    queryKey: [FILES_QUERY_KEY, Number(request.constructionSiteId), request.folderId],
    staleTime: 60 * 5 * 1000, // 5 minutes
    enabled: !!request.constructionSiteId && !!request.folderId,
    retry: 1,
    retryDelay: 1000,
    queryFn: async () => {
      const { userInfoTracking, currentTenantTracking, tenantIds } = request;
      delete request.userInfoTracking;
      delete request.currentTenantTracking;
      delete request.tenantIds;

      let lastUpdate: IUpdateStatus = {
        lastUpdate: (AppStorage.GetItem('lastUpdateFileList') as string) ?? '',
        status: 'atualizado'
      };

      lastUpdate = { ...lastUpdate, status: 'atualizando' };
      const beforeApiCall = dayjs();
      const isGoogleDriveExperimentOnTrmt = request.api === ApisEnum.GoogleDrive;

      const apiCall = isGoogleDriveExperimentOnTrmt
        ? ObrasApi.listGoogleDriveFiles
        : ObrasApi.listFiles;

      const requestApi: IListFilesRequest = {
        ...request,
        loadObsoleteFiles: request.folderId in loadObsoleteFiles
      };
      if (isGoogleDriveExperimentOnTrmt) {
        requestApi.googleDriveExperiment = 'test';
      }
      const response = await apiCall(requestApi);

      const apiResponseTime = dayjs().diff(beforeApiCall, 'milliseconds');

      Mixpanel.track({
        name: 'FILE_LIST_API_CALL',
        props: {
          elapsed: apiResponseTime,
          googleDriveExperiment: requestApi.googleDriveExperiment,
          api: requestApi.api
        },
        userInfo: userInfoTracking,
        currentListTenant: currentTenantTracking,
        tenantIds
      });

      if (response.data.data.LastUpdateDate) {
        lastUpdate = {
          ...lastUpdate,
          status: 'atualizado',
          lastUpdate: dayjs(response.data.data.LastUpdateDate)
            .toDate()
            .toDateString()
        };
      }
      if (response.data.data.CurrentCsUserPermissions) {
        dispatch(
          ListagemArquivosActions.currentUserPrivileges(
            response.data.data.CurrentCsUserPermissions
          )
        );
      }
      if (
        response.data.data.RequestManageObsoleteFiles &&
        !(request.folderId in loadObsoleteFiles)
      ) {
        setLoadObsoleteFiles((prevState) => {
          prevState[request.folderId] = true;
          return { ...prevState };
        });
      }
      const hasRenderFile =
        response.data.data.Files.findIndex((x) => x.RenderingStatus !== 0) !== -1;

      if (hasRenderFile) {
        const filesApiId = response.data.data.Files.filter(
          (x) => x.RenderingStatus === 2
        ).map((x) => x.Identifier.ApiId);
        setVerifyFilesIds(filesApiId);
      }

      let files: IFileData[] = [];
      if (
        response.data.data?.isObsoleteFiles &&
        response.data.data?.Files &&
        response.data.data.Files?.length > 0
      ) {
        for (const file of response.data.data.Files) {
          files.push({ ...file, Versions: [] });
          if (file?.Versions && file.Versions?.length > 0) {
            for (const olderFiles of file.Versions) {
              files.push(olderFiles);
            }
          }
        }
      } else {
        files = response.data.data.Files;
      }

      const filesList: IFileData[] = files;
      for (const fileRow of filesList) {
        if (!fileRow.RenderingSignalR) {
          fileRow.RenderingSignalR = {
            etapa: 0,
            progress: 0,
            fileIdentifier: fileRow.Identifier.ApiId || fileRow.FileInfo.ApiId
          };
        }
      }

      const returnResponse = {
        ListFiles: filesList,
        LastUpdate: lastUpdate
      } as IUseQueryFilesReturn;
      if (response.data.data.BreadCrumbItem) {
        returnResponse.BreadCrumbItem = response.data.data.BreadCrumbItem;
      }
      if (response.data.data.ObsoleteFolderId) {
        returnResponse.ObsoleteFolderId = response.data.data.ObsoleteFolderId;
      }
      if (response.data.data.ConstructionSite) {
        returnResponse.ConstructionSite = response.data.data.ConstructionSite;
      }
      if (response.data.data.CloudServiceFolderUrl) {
        returnResponse.CloudServiceFolderUrl = response.data.data.CloudServiceFolderUrl;
      }
      return returnResponse;
    }
  });

  const handleUpdateLastStatus = useCallback(async ({
    constructionSiteId,
    folderId,
    status,
    lastUpdate,
  }: IUpdateStatusStateRequest) => {
    await queryClient.cancelQueries({
      queryKey: [FILES_QUERY_KEY, Number(constructionSiteId), folderId],
    });
    queryClient.setQueryData<IUseQueryFilesReturn>(
      [FILES_QUERY_KEY, Number(constructionSiteId), folderId],
      (old) => {
        if (old) {
          const newLastUpdate = { ...old.LastUpdate } as IUpdateStatus;
          if (status) { newLastUpdate.status = status; }
          if (lastUpdate) { newLastUpdate.lastUpdate = lastUpdate.toDateString(); }
          old.LastUpdate = newLastUpdate;
        }
        return old;
      },
    );
  }, []);

  useEffect(() => {
    if (!data && !isFetching && error) {
      Mixpanel.track({
        name: 'ERROR_FILE_LIST_API_CALL',
        props: {
          googleDriveExperiment: request.googleDriveExperiment,
          api: request.api,
        },
        userInfo: request.userInfoTracking,
        currentListTenant: request.currentTenantTracking,
        tenantIds: request.tenantIds,
      });
      handleUpdateLastStatus({
        constructionSiteId: request.constructionSiteId,
        folderId: request.folderId,
        status: 'error',
      });

      const msg = 'Erro ao listar arquivos';
      const errorApi = (error as any).errors ? (error as any).errors[0]?.Message || msg : msg;
      toastHandler.showError(errorApi);
    }

    const handleUpdateObra = async (cs: IObraResponse) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: [OBRA_QUERY_KEY] }),
        queryClient.cancelQueries({ queryKey: [OBRA_QUERY_KEY, Number(cs.ConstructionSiteId)] }),
      ]);
      queryClient.setQueryData<IObraResponse[]>([OBRA_QUERY_KEY], (old) => {
        if (old) { return old; }
        return [cs];
      });
      queryClient.setQueryData<IObraResponse>([OBRA_QUERY_KEY, Number(cs.ConstructionSiteId)], (old) => {
        if (old) { return old; }
        return cs;
      });
    };
    if (
      data && !error && !isFetching &&
      data.ConstructionSite && data.ConstructionSite.ConstructionSiteId
    ) {
      handleUpdateObra(data.ConstructionSite);
    }
    if ((request.folderId in loadObsoleteFiles) && loadObsoleteFiles[request.folderId] && data && !isFetching) {
      isBackgroundRefetching.current = true;
      refetch().finally(() => {
        isBackgroundRefetching.current = false;
      });
      setLoadObsoleteFiles((prevState) => {
        if (request.folderId in prevState) {
          prevState[request.folderId] = false;
        }
        return { ...prevState };
      });
    }
  }, [
    data,
    dispatch,
    error,
    isFetching,
    request,
    loadObsoleteFiles,
    handleUpdateLastStatus,
    refetch,
  ]);

  useEffect(() => {
    if (data && !isFetching && !error && verifyFilesIds.length > 0) {
      for (const fileId of verifyFilesIds) {
        verifyAutodeskProfressFiles({
          constructionSiteId: request.constructionSiteId,
          folderId: request.folderId,
          fileApiId: fileId,
        });
      }
      setVerifyFilesIds([]);
    }
  }, [
    data,
    error,
    isFetching,
    request.constructionSiteId,
    request.folderId,
    verifyFilesIds,
    verifyAutodeskProfressFiles,
  ]);

  const dataDefault = useMemo(() => {
    const lastUpdateDefault: IUpdateStatus = {
      lastUpdate: AppStorage.GetItem('lastUpdateFileList') as string ?? '',
      status: 'atualizado',
    };
    return {
      ListFiles: [],
      BreadCrumbItem: null,
      ObsoleteFolderId: '',
      CloudServiceFolderUrl: '',
      ConstructionSite: null,
      LastUpdate: lastUpdateDefault,
    };
  }, []);

  return {
    data: data || dataDefault,
    refetch,
    isLoading,
    isFetching: isFetching && !isBackgroundRefetching.current,
    error,
  };
};

export const addNewFileInQuery = async (
  csId: number,
  folderId: string,
  newFile: IFileData,
) => {
  let currentFile = newFile;

  await queryClient.cancelQueries({
    queryKey: [FILES_QUERY_KEY, Number(csId), folderId],
  });

  // Monta a lista nova sem alterar o cache anterior nem reaproveitar o array
  // `Versions` de outro arquivo. Antes, `Versions ?? []` devolvia o próprio array
  // da versão anterior (o servidor manda `[]` em obra com revisão) e o arquivo
  // antigo era empurrado para dentro dele: a lista ficava circular, o structural
  // sharing do React Query estourava a pilha e, a partir daí, todo upload naquela
  // pasta quebrava no onFinish e travava a fila (obras 2183 e 1409, set-out/2026).
  queryClient.setQueryData<IUseQueryFilesReturn>(
    [FILES_QUERY_KEY, Number(csId), folderId],
    (old) => {
      if (!old) {
        return old;
      }

      if (currentFile.FileIdToOverwrite) {
        return {
          ...old,
          ListFiles: old.ListFiles.map((file) => {
            return file.Identifier.ApiId === currentFile.FileIdToOverwrite ? currentFile : file;
          }),
        };
      }

      let listFiles = old.ListFiles;

      if (currentFile.hasRevisionControl && currentFile.RevisionNumber) {
        const index = listFiles.findIndex((file) => {
          return file.NameWithoutRevision === currentFile.NameWithoutRevision;
        });
        if (index !== -1) {
          const previousFile = listFiles[index];
          const { Versions: previousVersions, ...previousWithoutVersions } = previousFile;

          if (previousFile.RevisionNumber > currentFile.RevisionNumber) {
            const { Versions: _ignored, ...currentWithoutVersions } = currentFile;
            currentFile = {
              ...previousFile,
              Versions: [...(previousVersions ?? []), currentWithoutVersions as IFileData],
            };
          } else {
            currentFile = {
              ...currentFile,
              Versions: [...(previousVersions ?? []), previousWithoutVersions as IFileData],
            };
          }
          listFiles = listFiles.filter((_, fileIndex) => fileIndex !== index);
        }
      }

      const newListFiles = [...listFiles];
      const insertIndex = newListFiles.findIndex(
        (file) => !file.IsFolder && file.Name.localeCompare(currentFile.Name) > 0,
      );
      if (insertIndex === -1) {
        newListFiles.push(currentFile);
      } else {
        newListFiles.splice(insertIndex, 0, currentFile);
      }
      return { ...old, ListFiles: newListFiles };
    },
  );
};

export const setUploadProgressFileInQuery = async (
  csId: number,
  folderId: string,
  progress: ISignalRArchive,
) => {
  await queryClient.cancelQueries({
    queryKey: [FILES_QUERY_KEY, Number(csId), folderId],
  });

  queryClient.setQueryData<IUseQueryFilesReturn>(
    [FILES_QUERY_KEY, Number(csId), folderId],
    (old) => {
      if (old) {
        const index = old.ListFiles.findIndex((file) => {
          return file.Identifier.ApiId === progress.fileIdentifier;
        });
        if (index !== -1) {
          old.ListFiles[index].UploadRenderingStatus = progress;
        }
        return { ...old };
      }
      return old;
    },
  );
};

export const setFileStatusRendering = async (
  request: IAutodeskViewer,
  isRerendering: boolean,
  engine: ViewerEngineName = 'autodesk',
) => {
  await queryClient.cancelQueries({
    queryKey: [FILES_QUERY_KEY, Number(request.constructionSiteId), request.folderId],
  });

  queryClient.setQueryData<IUseQueryFilesReturn>(
    [FILES_QUERY_KEY, Number(request.constructionSiteId), request.folderId],
    (old) => {
      if (old) {
        return {
          ...old,
          ListFiles: old.ListFiles.map((file) => {
            if (file.Identifier.ApiId === request.fileApiId) {
              Object.assign(file, withConversionStatus(file, engine, BucketFileStatusEnum.Rendering));
            }
            if (
              (!isRerendering && !file.RenderingSignalR) ||
              (isRerendering && file.RenderingSignalR.etapa === 5)
            ) {
              file.RenderingSignalR = {
                etapa: 0,
                progress: 0,
                fileIdentifier: file.Identifier.ApiId,
              };
            }
            return file;
          }),
        };
      }
      return old;
    },
  );
};
